import posthog, { PostHog, Properties } from 'posthog-js';
import { environment } from '../environment';
import { REPLAY_BLOCK_SELECTOR } from './replayPrivacy';

const encoder = new TextEncoder();

/**
 * OFF while Sentry Replay (error-only) is the single recorder — two rrweb instances mirroring the
 * same DOM was a major heap-growth source. Flip to true to re-enable; the block selector and
 * sample rate below already apply.
 */
const POSTHOG_SESSION_RECORDING_ENABLED = false;

/** Fraction of sessions recorded once re-enabled. Takes precedence over PostHog's remote config. */
const POSTHOG_SESSION_RECORDING_SAMPLE_RATE = 0.1;

/** Longer than this and the host is treated as slow, not blocked. */
const HOST_PROBE_TIMEOUT_MS = 4_000;

/** Gap before the second probe. Only a fast refusal gets retried, so this is the whole added cost. */
const HOST_PROBE_RETRY_DELAY_MS = 1_500;

/**
 * Onboarding funnel, captured as `onboarding_<step>`:
 *   connect_modal_opened → wallet_connected → terms_shown → authenticated → bridge_opened / bridge_completed
 *   → deposit_started → deposit_confirmed → trade_placed (success)
 * (`wallet_connected` and `trade_placed` are the pre-existing events.) Only progress steps are emitted — a
 * missing next step is the drop-off — plus one `failed` event for stops the user did not choose (access gate,
 * restriction, geo-block, backend / bridge / transaction errors), broken down by its `step` and `reason`.
 * Steps before `wallet_connected` are anonymous events; PostHog links them to the person on identify().
 */
export type OnboardingStep =
  | 'connect_modal_opened'
  | 'terms_shown'
  | 'authenticated'
  | 'bridge_opened'
  | 'bridge_completed'
  | 'deposit_started'
  | 'deposit_confirmed'
  | 'failed';

/**
 * 'idle' = no PostHog key configured. 'probing' = one request to the events endpoint is in flight and calls
 * made meanwhile are buffered. 'blocked' = that request was refused at the network level (ad blocker, CSP, DNS),
 * so the SDK is never initialised — it would otherwise retry every batch and fill the console with failed requests.
 */
type TelemetryState = 'idle' | 'probing' | 'ready' | 'blocked';

/**
 * Telemetry Facade
 * Abstracting specific telemetry provider (PostHog) to allow easy replacement.
 */
class TelemetryService {
  private client: PostHog = posthog;
  private currentTheme: 'dark' | 'light' | null = null;
  private state: TelemetryState = 'idle';
  private pending: Array<() => void> = [];
  /** Bumped by every reset(), so a wallet identity still being hashed can tell it is stale. */
  private identityEpoch = 0;

  init(apiKey: string, apiHost: string): void {
    this.state = 'probing';
    void this.isHostReachable(apiKey, apiHost).then((reachable) => {
      if (!reachable) {
        this.state = 'blocked';
        this.pending = [];
        return;
      }
      this.client.init(apiKey, {
        api_host: apiHost,
        ui_host: 'https://eu.posthog.com',
        defaults: '2025-11-30',
        person_profiles: 'identified_only', // Only create profiles for identified users
        disable_surveys: false, // Enable surveys for feedback collection
        autocapture: false, // DISABLE: Prevents automatic tracking of every click
        capture_pageview: false, // DISABLE: We will trigger views manually if needed
        persistence: 'localStorage',
        advanced_disable_decide: false,
        disable_session_recording: true, // Start disabled — enable via loaded callback to avoid race condition
        capture_performance: true,
        session_recording: {
          // Keep the chart and order book out of the mirror even when recording is on — see `replayPrivacy.ts`.
          blockSelector: REPLAY_BLOCK_SELECTOR,
          sampleRate: POSTHOG_SESSION_RECORDING_SAMPLE_RATE,
        },
        loaded: (ph) => {
          if (!POSTHOG_SESSION_RECORDING_ENABLED) {
            return;
          }
          const isPhone = /Android|iPhone|iPod/i.test(navigator.userAgent);
          if (!isPhone) {
            ph.startSessionRecording();
          }
        },
      });
      this.state = 'ready';
      const queued = this.pending;
      this.pending = [];
      for (const run of queued) run();
    });
  }

  /**
   * Two attempts, because one fast failure does not mean blocked: a DNS miss, a Wi-Fi
   * re-association after wake, or a proxy 52x all reject exactly like an ad blocker, and calling it
   * blocked disables telemetry for the rest of the tab. A blocker refuses instantly and refuses
   * again, so the retry costs the delay and nothing else.
   */
  private async isHostReachable(apiKey: string, apiHost: string): Promise<boolean> {
    if (await this.probeHost(apiKey, apiHost)) return true;
    await new Promise((resolve) => setTimeout(resolve, HOST_PROBE_RETRY_DELAY_MS));
    return this.probeHost(apiKey, apiHost);
  }

  /**
   * Probes the SDK's own first request — the remote-config fetch every `init()` starts with, which on a
   * custom `api_host` resolves against that host. Sent `no-cors` because the probe reads nothing: only a
   * network-level refusal (extension, CSP, DNS) rejects, while a response missing CORS headers stays
   * opaque and still counts as reachable. Any HTTP response counts as reachable; so do a timeout (slow
   * network) and being offline (the SDK queues).
   */
  private async probeHost(apiKey: string, apiHost: string): Promise<boolean> {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), HOST_PROBE_TIMEOUT_MS);
    try {
      await fetch(`${apiHost}/array/${apiKey}/config`, {
        method: 'GET',
        mode: 'no-cors',
        cache: 'no-store',
        signal: controller.signal,
      });
      return true;
    } catch {
      return controller.signal.aborted;
    } finally {
      clearTimeout(timer);
    }
  }

  /** Buffers `run` while the probe is in flight; true tells the caller to stop. */
  private defer(run: () => void): boolean {
    if (this.state !== 'probing') return false;
    this.pending.push(run);
    return true;
  }

  private isEnabled(): boolean {
    return this.state === 'ready' && !!this.client.__loaded;
  }

  logEvent(eventName: string, properties?: Properties): void {
    if (this.defer(() => this.logEvent(eventName, properties))) return;
    if (!this.isEnabled()) return;
    this.client.capture(eventName, properties);
  }

  initializeSession(sessionHash: string, properties?: Properties): void {
    if (this.defer(() => this.initializeSession(sessionHash, properties))) return;
    if (!this.isEnabled()) return;
    this.client.identify(sessionHash, properties);
  }

  setSessionConfig(properties: Properties): void {
    if (this.defer(() => this.setSessionConfig(properties))) return;
    if (!this.isEnabled()) return;
    this.client.register(properties);
  }

  reset(): void {
    // Bumped on the caller's turn, not on replay, so a wallet connection whose session hash is still
    // being computed drops itself instead of identifying the browser as the wallet it just left.
    this.identityEpoch += 1;
    this.applyReset();
  }

  private applyReset(): void {
    if (this.defer(() => this.applyReset())) return;
    if (!this.isEnabled()) return;
    this.logEvent('wallet_disconnected');
    this.client.reset();
    sessionStorage.removeItem('telemetry_wallet_connected');
  }

  private getInstalledWallets(): string[] {
    const wallets: string[] = [];

    // Common Solana Injections
    if (window.solana && window.solana.isPhantom) wallets.push('Phantom');
    if (window.solflare && window.solflare.isSolflare) wallets.push('Solflare');
    if (window.backpack) wallets.push('Backpack');

    // Common EVM Injections
    if (window.ethereum && window.ethereum.isMetaMask) wallets.push('MetaMask');
    if (window.ethereum && (window.ethereum as any).isRabby) wallets.push('Rabby');
    if (window.ethereum && (window.ethereum as any).isTrust) wallets.push('Trust Wallet');
    if (window.BinanceChain) wallets.push('Binance Wallet');
    if (window.bitkeep) wallets.push('BitKeep');
    if (window.ethereum && (window.ethereum as any).isZerion) wallets.push('Zerion');
    if (window.ethereum && (window.ethereum as any).isRainbow) wallets.push('Rainbow');
    if (window.okxwallet) wallets.push('OKX');
    if (window.coinbaseWalletExtension) wallets.push('Coinbase');

    return wallets.length > 0 ? wallets : ['Unknown'];
  }

  private async getSessionHash(walletAddress: string): Promise<string> {
    const text = walletAddress + environment.analyticsSalt;

    const hashBuffer = await crypto.subtle.digest('SHA-256', encoder.encode(text));
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  }

  /**
   * Hashes before it queues: the replay loop runs its entries synchronously, so an `await` inside a
   * queued closure would let everything queued behind it — a logout's reset(), the next funnel step —
   * run before this identify lands. With the hash in hand the whole identity update is one sync step.
   */
  async logWalletConnection(walletAddress: string, walletProvider: string, network: string) {
    if (this.state === 'idle') return;
    const epoch = this.identityEpoch;
    const sessionHash = await this.getSessionHash(walletAddress);
    if (epoch !== this.identityEpoch) return; // disconnected while hashing
    this.applyWalletConnection(sessionHash, walletAddress, walletProvider, network);
  }

  private applyWalletConnection(sessionHash: string, walletAddress: string, walletProvider: string, network: string): void {
    if (this.defer(() => this.applyWalletConnection(sessionHash, walletAddress, walletProvider, network))) return;
    if (!this.isEnabled()) return;

    const detectedWallets = this.getInstalledWallets();

    // Capture onboarding context from URL
    const searchParams = new URLSearchParams(window.location.search);
    const refCode = searchParams.get('ref') || searchParams.get('ref_code');
    const utmSource = searchParams.get('utm_source');
    const utmMedium = searchParams.get('utm_medium');
    const utmCampaign = searchParams.get('utm_campaign');

    // 1. Initialize Session (carry theme so it lands on the profile at identify-time)
    this.initializeSession(sessionHash, this.currentTheme ? { theme: this.currentTheme } : undefined);

    // 2. Set Session Config (Global properties for all events in this session)
    const sessionConfig: Properties = {
      wallet_provider: walletProvider,
      network: network,
      installed_wallets: detectedWallets,
      is_hashed_session: true,
    };

    if (refCode) sessionConfig.ref_code = refCode;
    if (utmSource) sessionConfig.utm_source = utmSource;
    if (utmMedium) sessionConfig.utm_medium = utmMedium;
    if (utmCampaign) sessionConfig.utm_campaign = utmCampaign;

    this.setSessionConfig(sessionConfig);

    // 3. Log Event (Only once per session to avoid noise on reload)
    const storageKey = `telemetry_wallet_connected`;
    const lastLoggedAddress = sessionStorage.getItem(storageKey);

    if (lastLoggedAddress !== walletAddress) {
      this.logEvent('wallet_connected');
      sessionStorage.setItem(storageKey, walletAddress);
    }
  }

  logDeposit(asset: string, valueUsd: number) {
    this.logEvent('deposit_submitted', {
      asset,
      value_usd: valueUsd,
    });
  }

  logPageView(path: string) {
    if (this.defer(() => this.logPageView(path))) return;
    if (!this.isEnabled()) return;
    this.client.capture('$pageview', {
      $current_url: window.location.href,
      path,
    });
  }

  logTrade(details: { market: string; side: string; leverage: number; value_usd: number; order_type: string }) {
    this.logEvent('trade_placed', details);
  }

  /**
   * Lifetime trading history, straight from the exchange's account stats. This is what the "already
   * trades" cohorts read, and deliberately not a flag set by logTrade: that fires when the order message
   * is sent rather than when the engine accepts it, and also on reposts and lifecycle retries, so it
   * would mark a wallet as a trader on an order the engine went on to reject.
   */
  setTraderProfile(profile: { hasTraded: boolean; totalTrades: number; lifetimeVolumeUsd: number }): void {
    if (this.defer(() => this.setTraderProfile(profile))) return;
    if (!this.isEnabled()) return;
    this.client.setPersonProperties({
      has_traded: profile.hasTraded,
      total_trades: profile.totalTrades,
      lifetime_volume_usd: profile.lifetimeVolumeUsd,
    });
  }

  logWithdrawal(asset: string, valueUsd: number) {
    this.logEvent('withdrawal_submitted', {
      asset,
      value_usd: valueUsd,
    });
  }

  logMarginAddition(details: { market: string; value_usd?: number; symbol: string }) {
    this.logEvent('margin_added', details);
  }

  logOnboarding(step: OnboardingStep, properties?: Properties): void {
    this.logEvent(`onboarding_${step}`, properties);
  }

  /**
   * A funnel failure that re-derives itself from backend config on every page load — an authority
   * block, for one — and so would otherwise report a fresh drop-off on every refresh by a visitor who
   * never entered the funnel. Deduped per browser session; a new tab reports again.
   */
  logOnboardingFailureOnce(dedupeKey: string, properties: Properties): void {
    const storageKey = `telemetry_onboarding_failed_${dedupeKey}`;
    try {
      if (sessionStorage.getItem(storageKey)) return;
      sessionStorage.setItem(storageKey, '1');
    } catch {
      // Site data blocked — a duplicate beats losing the signal.
    }
    this.logOnboarding('failed', properties);
  }

  setTheme(theme: 'dark' | 'light'): void {
    this.currentTheme = theme;
    if (this.defer(() => this.setTheme(theme))) return;
    if (!this.isEnabled()) return;
    this.client.setPersonProperties({ theme });
  }

  // Both hash before queueing, for the ordering reason on logWalletConnection; logEvent does the buffering.
  async logTermsAccepted(address: string) {
    if (this.state === 'idle') return;
    const sessionHash = await this.getSessionHash(address);
    this.logEvent('terms_accepted', { address: sessionHash });
  }

  async logRestrictedAccess(address: string) {
    if (this.state === 'idle') return;
    const sessionHash = await this.getSessionHash(address);
    this.logEvent('restricted_access', { address: sessionHash });
  }

  /** Configured (a key is set), regardless of whether the host turned out to be reachable. */
  isLoaded(): boolean {
    return this.state !== 'idle';
  }
}

export const telemetry = new TelemetryService();
